import * as React from "react";

import { useAuth } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import type { ViewRecord } from "@/shared/domain/views";

import type { ResourceView } from "../useResourceView";
import type { ResourceViewState } from "../viewSerialization";

import { isViewDirty } from "./isViewDirty";
import { useApplyView, type ApplyViewOptions } from "./useApplyView";
import { viewLinkFor } from "./useViewDeepLink";

export interface LoadedView {
  /** The View currently applied, or `null` for ad-hoc filters. */
  loadedView: ViewRecord | null;
  /**
   * Apply a View and mark it loaded.
   *
   * Resolves `true` when the View was applied and is now loaded, `false` when
   * the apply stood down — superseded by a newer one, or abandoned because the
   * customer moved the view themselves. Callers that mirror the loaded View
   * elsewhere, such as the `?view=` deep link, need the outcome: watching
   * `loadedView` alone cannot tell "not yet" from "never".
   */
  selectView: (record: ViewRecord) => Promise<boolean>;
  /** Unload the current View and clear filters. */
  clearView: () => void;
  /**
   * Throw away edits and put the loaded View's saved state back. No-op with
   * nothing loaded.
   *
   * Lives here rather than in the Save control so that discarding and
   * selecting share one applier. Two appliers over the same table carry two
   * counters, and neither can tell whether the other is older or newer — so a
   * Discard resolving out of order either overwrites a newer selection or
   * makes that selection stand down for it.
   */
  discardChanges: () => void;
  /**
   * Open a bracket around an operation that will mark a View loaded when it
   * finishes. Call before the request goes out; hand the token back to
   * {@link LoadedView.completeSave}.
   *
   * Exists because a save spans two owners — `useSaveView` starts it, this hook
   * concludes it — so neither could judge on its own whether the world had
   * moved in between. The token is that judgement, captured at the only moment
   * it can be.
   */
  beginSave: () => number;
  /**
   * Mark a View loaded without applying it — for one just created *from* the
   * current state, where re-applying would only reproduce what is on screen
   * (and briefly clear the filters on the way).
   *
   * `token` comes from {@link LoadedView.beginSave}. No-ops when anything
   * invalidated the save while it was out, on either of two axes:
   *
   *  - the **generation moved** — an org switch, a Clear, a newer View applied.
   *    None of these need change the table, which is exactly why the content
   *    check below cannot see them.
   *  - the **table moved** — the customer edited a filter, re-sorted, changed
   *    columns. None of these bump the generation, because they never pass
   *    through this hook.
   *
   * Two axes, two checks, mirroring `applyView`, which guards the same pair for
   * the same reason. Either one alone leaves a hole.
   */
  completeSave: (record: ViewRecord, token: number) => void;
  /** The last apply failure, or `null`. */
  error: Error | null;
  /** Absolute URL that reopens a View, for the Copy link action. */
  linkFor: (view: ViewRecord) => string;
  /**
   * The View the table was *born* showing, when a `?view=` link was resolved
   * before mount, or `undefined`. Hand it to `useViewDeepLink`, which uses it
   * to skip a resolve-and-apply that would only reproduce what is on screen.
   */
  seededViewId: string | undefined;
}

/**
 * Own which View a resource view has loaded.
 *
 * Lives here rather than in `ViewPicker` because a View is also unloaded by
 * routes the picker never sees — Clear, removing the last chip — which the
 * emptiness invariant below covers.
 *
 * A View is marked loaded only *after* its state is applied. Applying a RoboQL
 * View is async, so setting it first opens a window where a just-loaded View
 * reads as dirty and the UI offers to save changes nobody made.
 *
 * `seededView` is the one exception, and it does not break that rule: a seeded
 * View was applied before this hook existed for this mount — `useViewFromLink`
 * built the resource view out of it — so it is loaded on the first render with
 * no window to be dirty in.
 *
 * `options` go to the applier; see `ApplyViewOptions` for the RoboQL flag.
 */
export function useLoadedView<TData>(
  view: ResourceView<TData>,
  seededView: ViewRecord | null = null,
  options?: ApplyViewOptions,
): LoadedView {
  // Seeded, not applied: `useViewFromLink` built the table out of this record
  // before the first render, so it is already on screen and marking it loaded is
  // a statement of fact rather than the end of an apply. Initial state only —
  // every later change to which View is loaded comes through the callbacks below.
  const [loadedView, setLoadedView] = React.useState<ViewRecord | null>(
    seededView,
  );
  const { applyView, supersede, generation, error } = useApplyView(
    view,
    options,
  );
  const { currentOrganization } = useAuth();
  const notify = useNotification();

  // A failed apply leaves the table as it was, which looks like nothing
  // happened. Tell the customer why, here rather than in each list: the picker
  // and the deep link both select through this hook, and neither has a place
  // of its own to show it. `error` is a fresh instance per failure and is
  // cleared when the next apply starts, so this fires once per failure.
  React.useEffect(() => {
    if (error !== null) {
      notify(error.message, "error");
    }
  }, [error, notify]);

  // The view's state as of the last render, for callbacks that resolve after an
  // await and so must judge the table as it is now rather than as it was when
  // they were created. `markLoaded` is the one that needs it.
  const filtersType = view.filters.type;
  const { filters, table } = view;
  const { sort, pageSize, visibleColumnIds } = table;
  const liveStateRef = React.useRef<ResourceViewState>({
    filters,
    sort,
    pageSize,
    visibleColumns: [...visibleColumnIds],
  });
  // A layout effect, for the same reason the org switch below uses one: this
  // ref is read by a callback that resumes from a network response, so it must
  // never lag what is on screen. A passive effect is only *scheduled* at
  // commit, which leaves a window where the customer's change is rendered but
  // this snapshot still describes the state before it — and a save landing in
  // that window would compare against the stale copy, find a match, and mark a
  // View loaded over a table that has moved on. Running in the commit closes
  // the window: by the time any continuation can execute, this is current.
  React.useLayoutEffect(() => {
    liveStateRef.current = {
      filters,
      sort,
      pageSize,
      visibleColumns: [...visibleColumnIds],
    };
  });

  const selectView = React.useCallback(
    (record: ViewRecord) => {
      // Only on success: otherwise the trigger names a View the table is not
      // showing. `applyView` also reports `false` when a newer selection has
      // overtaken this one, which is what keeps two quick picks from landing
      // out of order.
      return applyView(record).then((applied) => {
        if (applied) {
          setLoadedView(record);
        }
        return applied;
      });
    },
    [applyView],
  );

  const { clearFilters } = view;
  const clearView = React.useCallback(() => {
    // Abandon any transpile still running, or it would apply its filters after
    // the clear and put the View back.
    supersede();
    setLoadedView(null);
    clearFilters();
  }, [clearFilters, supersede]);

  const discardChanges = React.useCallback(() => {
    if (loadedView !== null) {
      // Re-applying the loaded View is the reset: it restores filters, sort,
      // page size and columns from the saved definition.
      void applyView(loadedView);
    }
  }, [applyView, loadedView]);

  const beginSave = React.useCallback(() => generation(), [generation]);

  const completeSave = React.useCallback(
    (record: ViewRecord, token: number) => {
      // Generation first, and before any `supersede` below — superseding bumps
      // the counter, so a bump here would make every comparison fail.
      //
      // This is the axis a content check cannot cover. An org switch is the
      // case that proved it: it drops the loaded marker but deliberately leaves
      // the filters alone, so the record still matches the table perfectly and
      // the check below waves it through — restoring a View from an org the
      // customer has left, in a picker and a `?view=` where it does not exist.
      if (token !== generation()) {
        return;
      }

      // The other axis. Saving is a server round trip, and the customer can
      // keep working while it is out — clear the table, edit a filter, re-sort.
      // None of those pass through this hook, so none bump the generation; the
      // record comes back describing the state the request was built from, and
      // may no longer describe the table.
      //
      // The test is the same one the UI uses to decide a View has unsaved
      // changes, which is what makes it the right one: marking a record loaded
      // that reads as dirty against the live state is precisely the claim
      // "the table is showing this View" being false. It subsumes the cleared
      // table, which is dirty against any View — a View always has filters.
      if (isViewDirty(record.definition, liveStateRef.current)) {
        return;
      }

      // Only now, and only on the path that actually marks: this View is about
      // to be on screen, and a late-landing apply would overwrite it. Standing
      // down an apply for a save we just refused would discard work the
      // customer still wants.
      supersede();
      setLoadedView(record);
    },
    [generation, supersede],
  );

  // A View belongs to the org it was written in: a `view_id` is globally
  // unique but reads as absent anywhere else, so one carried across a switch
  // describes nothing in the org just entered. Superseding as well as dropping
  // the marker covers an apply still in flight — an org switch starts no second
  // apply and moves no filters, sort, page size or columns, so it is invisible
  // to every guard in `useApplyView`.
  //
  // The filters are deliberately left alone. They are the customer's own query,
  // they mean the same thing in either org, and clearing the table under
  // someone who only changed org would be the more surprising outcome; without
  // the marker they are simply ad-hoc filters again.
  //
  // Keyed off a ref rather than the effect firing, because an effect on the org
  // id also runs on mount — and under StrictMode runs twice — which would
  // supersede an apply the deep link had just started.
  //
  // A layout effect rather than a passive one, which is load-bearing rather
  // than stylistic. The switch starts in a click, so React commits it
  // synchronously within that event and runs layout effects before the task
  // ends; a passive effect is scheduled and runs after the microtask
  // checkpoint. A transpile resolving in that gap would find the counter
  // un-bumped and write the old org's state to the new org's table. Superseding
  // in the commit puts this ahead of any continuation the switch could race.
  const orgId = currentOrganization?.org_id;
  const orgIdRef = React.useRef(orgId);
  React.useLayoutEffect(() => {
    if (orgIdRef.current === orgId) {
      return;
    }
    orgIdRef.current = orgId;
    supersede();
    setLoadedView(null);
  }, [orgId, supersede]);

  // A View is its filters, so a table showing none has no View on it. Held as
  // an invariant rather than wired into each Clear button, because routes that
  // empty the filters (the chip bar's Clear, removing the last chip) call
  // `clearFilters` straight through and never reach this hook.
  //
  // Safe to key on emptiness: a filterless View cannot exist, since the Save
  // control lives inside `ActiveFilters`, which renders nothing when empty.
  React.useEffect(() => {
    if (filtersType === "none") {
      // Stand down any transpile still running as well as dropping the marker.
      // Emptying the filters is identity-stable when they were already empty
      // (one shared `NONE_STATE`), so `useApplyView`'s own check cannot see
      // every case of this on its own.
      supersede();
      setLoadedView(null);
    }
  }, [filtersType, supersede]);

  // Read once: the seed is a mount-time fact, and a later render passing a
  // different record would otherwise tell the deep link to stop honouring a
  // link it has not yet resolved.
  const [seededViewId] = React.useState(() => seededView?.view_id);

  return {
    loadedView,
    selectView,
    clearView,
    discardChanges,
    beginSave,
    completeSave,
    error,
    linkFor: viewLinkFor,
    seededViewId,
  };
}
